import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';

import { DeviceInfo } from '../models/device-info.model';

@Injectable({
  providedIn: 'root'
})
export class DeviceInfoService {
  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);

  getDeviceInfo(): Observable<DeviceInfo> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(this.getDefaultDeviceInfo());
    }

    const deviceInfo: DeviceInfo = {
      ip: '',
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      operatingSystem: this.getOperatingSystem(),
      browser: this.getBrowserInfo().name,
      browserVersion: this.getBrowserInfo().version,
      deviceType: this.getDeviceType(),
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      timestamp: new Date().toISOString()
    };

    return this.getUserIP().pipe(
      map(ip => ({ ...deviceInfo, ip })),
      catchError(() => of(deviceInfo))
    );
  }


  private getOperatingSystem(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.indexOf('Windows NT 10.0') !== -1) return 'Windows 10';
    if (userAgent.indexOf('Windows NT 6.3') !== -1) return 'Windows 8.1';
    if (userAgent.indexOf('Windows NT 6.2') !== -1) return 'Windows 8';
    if (userAgent.indexOf('Windows NT 6.1') !== -1) return 'Windows 7';
    if (userAgent.indexOf('Windows NT 6.0') !== -1) return 'Windows Vista';
    if (userAgent.indexOf('Windows NT 5.1') !== -1) return 'Windows XP';
    if (userAgent.indexOf('Windows NT 5.0') !== -1) return 'Windows 2000';
    if (userAgent.indexOf('Mac OS X') !== -1) return 'macOS';
    if (userAgent.indexOf('Mac OS') !== -1) return 'Mac OS';
    if (userAgent.indexOf('X11') !== -1) return 'UNIX';
    if (userAgent.indexOf('Linux') !== -1) return 'Linux';
    if (userAgent.indexOf('Android') !== -1) return 'Android';
    if (userAgent.indexOf('iPhone') !== -1) return 'iOS';
    if (userAgent.indexOf('iPad') !== -1) return 'iOS';
    
    return 'Unknown';
  }


  private getBrowserInfo(): { name: string; version: string } {
    const userAgent = navigator.userAgent;
    
    // Chrome
    if (userAgent.indexOf('Chrome') !== -1 && userAgent.indexOf('Edg') === -1) {
      const version = userAgent.match(/Chrome\/(\d+\.\d+)/);
      return { name: 'Chrome', version: version ? version[1] : 'Unknown' };
    }
    
    // Edge
    if (userAgent.indexOf('Edg') !== -1) {
      const version = userAgent.match(/Edg\/(\d+\.\d+)/);
      return { name: 'Edge', version: version ? version[1] : 'Unknown' };
    }
    
    // Firefox
    if (userAgent.indexOf('Firefox') !== -1) {
      const version = userAgent.match(/Firefox\/(\d+\.\d+)/);
      return { name: 'Firefox', version: version ? version[1] : 'Unknown' };
    }
    
    // Safari
    if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) {
      const version = userAgent.match(/Version\/(\d+\.\d+)/);
      return { name: 'Safari', version: version ? version[1] : 'Unknown' };
    }
    
    // Internet Explorer
    if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident') !== -1) {
      const version = userAgent.match(/(?:MSIE |rv:)(\d+\.\d+)/);
      return { name: 'Internet Explorer', version: version ? version[1] : 'Unknown' };
    }
    
    return { name: 'Unknown', version: 'Unknown' };
  }

  private getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
    const userAgent = navigator.userAgent;
    
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      if (/iPad|Android(?!.*Mobile)/i.test(userAgent)) {
        return 'tablet';
      }
      return 'mobile';
    }
    
    return 'desktop';
  }

  private getDefaultDeviceInfo(): DeviceInfo {
    return {
      ip: '',
      userAgent: 'Server-Side Rendering',
      platform: 'Unknown',
      operatingSystem: 'Unknown',
      browser: 'Unknown',
      browserVersion: 'Unknown',
      deviceType: 'desktop',
      screenResolution: 'Unknown',
      timezone: 'UTC',
      language: 'pt-BR',
      timestamp: new Date().toISOString()
    };
  }

  private getUserIP(): Observable<string> {
    return this.http.get<{origin: string}>('https://httpbin.org/ip').pipe(
      timeout(5000),
      map(response => response.origin?.split(',')[0]?.trim() || ''),
      catchError(() => {
        return this.http.get<{ip: string}>('https://ipapi.co/json').pipe(
          timeout(5000),
          map(response => response.ip || ''),
          catchError(() => of(''))
        );
      })
    );
  }
}
